import { Box } from '@mui/material';
import { AUTH_TOKENS } from './auth-branding';

export type AuthMode = 'signup' | 'signin';

interface AuthModeToggleProps {
  mode: AuthMode;
  onChange: (mode: AuthMode) => void;
}

/**
 * Segmented "Sign up / Sign in" control. A white pill slides under the active
 * label via transform (translateX 0 -> 100%). Values map to the design handoff:
 * track 46px / radius 12px / grey-100 bg; pill 36px / radius 9px.
 */
export const AuthModeToggle = ({ mode, onChange }: AuthModeToggleProps) => {
  const options: { value: AuthMode; label: string }[] = [
    { value: 'signup', label: 'Sign up' },
    { value: 'signin', label: 'Sign in' },
  ];

  const buttonSx = (active: boolean) => ({
    position: 'relative',
    zIndex: 1,
    flex: 1,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    fontSize: '0.9375rem',
    color: active ? 'text.primary' : AUTH_TOKENS.inkSecondary,
    transition: 'color 0.2s',
  });

  return (
    <Box
      role="group"
      aria-label="Sign up or sign in"
      sx={{
        position: 'relative',
        display: 'flex',
        height: 46,
        p: '4px',
        mb: '28px',
        borderRadius: '12px',
        backgroundColor: 'lightGrey.light',
        border: '1px solid',
        borderColor: AUTH_TOKENS.border,
        boxSizing: 'border-box',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          height: 36,
          width: 'calc(50% - 4px)',
          borderRadius: '9px',
          backgroundColor: 'common.white',
          boxShadow: '0 1px 3px rgba(17,30,33,0.12)',
          transition: 'transform 0.24s cubic-bezier(0.4,0,0.2,1)',
          transform: mode === 'signup' ? 'translateX(0)' : 'translateX(100%)',
        }}
      />
      {options.map((option) => {
        const active = mode === option.value;
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            sx={buttonSx(active)}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
};
